import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  ProductSchema,
  ProductCreateInputSchema,
  ProductUpdateInputSchema,
  type Product,
  type ProductCreateInput,
  type ProductUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `product` (élément de catalogue, org-wide).
 *
 * Calque le PATRON asset : l'entrée est VALIDÉE par Zod
 * (`ProductCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide.
 */

export type { ProductCreateInput };

/** Nom de la collection Firestore de la famille product. */
export const PRODUCTS_COLLECTION = 'products';

/**
 * Repository typé pour la famille `product`.
 * Toute lecture est validée par `ProductSchema`, toute écriture par
 * `ProductCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ProductRepository {
  create(input: ProductCreateInput): Promise<Product>;
  update(id: string, patch: ProductUpdateInput): Promise<Product>;
  list(options?: ListOptions): Promise<Product[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Product | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Product>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Product>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Product>;
}

const productConverter = createConverter<Product>((raw: DocumentData) => ProductSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createProductRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ProductRepository {
  const col = collection(db, PRODUCTS_COLLECTION).withConverter(productConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Product>(db, col, PRODUCTS_COLLECTION, getActorId);

  return {
    async create(input: ProductCreateInput): Promise<Product> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = ProductCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.sku !== undefined) optional['sku'] = fields.sku;
      if (fields.basePrice !== undefined) optional['basePrice'] = fields.basePrice;
      if (fields.taxRate !== undefined) optional['taxRate'] = fields.taxRate;
      if (fields.unit !== undefined) optional['unit'] = fields.unit;
      if (fields.variants !== undefined) optional['variants'] = fields.variants;
      if (fields.trackStock !== undefined) optional['trackStock'] = fields.trackStock;
      if (fields.durationMinutes !== undefined) optional['durationMinutes'] = fields.durationMinutes;
      if (fields.priceListId !== undefined) optional['priceListId'] = fields.priceListId;
      if (fields.compositionId !== undefined) optional['compositionId'] = fields.compositionId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Product);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return ProductSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: ProductUpdateInput): Promise<Product> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = ProductUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, PRODUCTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec ProductSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`product ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Product[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Product | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
